import { Router } from '@lightningjs/sdk'

import {
  ACCOUNT_DETAILS,
  ADD_FAVORITES_ENTITY_SEARCH,
  ADD_FAVORITES_SPORT_PICKER,
  BASE_ROUTES,
  BOOT,
  COLLECTION,
  CUSTOMER_SUPPORT,
  DEVELOPER_SETTINGS,
  EDIT_FAVORITES,
  ERROR,
  EVENT_HUB,
  FAVORITE_HUB,
  FIRST_LANDING,
  FORGOT_PASSWORD,
  HOME,
  LANDING,
  LG_SIGN_UP,
  LIVE_SCORES,
  LOGIN,
  NAV_HUB,
  NOT_FOUND,
  PAYWALL,
  PLAYER,
  PRIVACY_POLICY,
  SEARCH,
  SETTINGS,
  SIGN_UP,
  SPLASH,
  SPORTS,
  SPORT_FILTER,
  SUBSCRIPTION,
  TERMS_OF_SERVICE,
  VERTICAL_HOME,
  VIZIO_SIGN_UP,
} from '../constants/routes'
import {
  AccountDetails,
  AddFavoritesEntitySearch,
  AddFavoritesSportPicker,
  Collection,
  CustomerSupport,
  DeveloperSettings,
  EditFavorites,
  Error,
  EventHub,
  FavoriteHub,
  FirstLanding,
  ForgotPassword,
  Home,
  Landing,
  LgSignUp,
  LiveScores,
  Login,
  NavHub,
  NotFound,
  Paywall,
  Player,
  PrivacyPolicy,
  Search,
  Settings,
  SignUp,
  Splash,
  SportFilter,
  Sports,
  Subscription,
  TermsOfService,
  VizioSignUp,
} from '../pages'
import { AppConfig } from '../services/appConfig'
import { composeUrl } from '../services/flosports/floExperience'
import { isUserSignedIn } from '../utils/authentication'
import { isObjEmpty } from '../utils/misc'
import { Menu } from '../widgets'

const staticRoutes = [
  {
    path: SPLASH,
    component: Splash,
  },
  {
    path: FIRST_LANDING,
    component: FirstLanding,
  },
  {
    path: LANDING,
    component: Landing,
  },
  {
    path: LOGIN,
    component: Login,
    widgets: ['Loading', 'Modal'],
  },
  {
    path: FORGOT_PASSWORD,
    component: ForgotPassword,
  },
  {
    path: HOME,
    component: Home,
    widgets: ['Menu', 'Loading'],
    options: {
      clearHistory: true,
    },
    beforeNavigate: async () => {
      // When we do a login from settings we are redirected to the home page but
      // the menu still had the settings button selected so we force the home
      // item instead to be selected here
      const activePage = Router.getActivePage()
      const menu = activePage?.widgets?.menu as Menu
      menu?.selectItem()
      return true
    },
  },
  {
    path: VERTICAL_HOME,
    component: Home,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: NAV_HUB,
    component: NavHub,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: EDIT_FAVORITES,
    component: EditFavorites,
    widgets: ['Menu'],
  },
  {
    path: ADD_FAVORITES_SPORT_PICKER,
    component: AddFavoritesSportPicker,
  },
  {
    path: ADD_FAVORITES_ENTITY_SEARCH,
    component: AddFavoritesEntitySearch,
  },
  {
    path: PAYWALL,
    component: Paywall,
    beforeNavigate: async (from, to) => {
      return isUserSignedIn()
        ? true
        : { path: LANDING, params: { siteId: to.register?.get('siteId') } }
    },
  },
  {
    path: PLAYER,
    component: Player,
    widgets: ['Loading', 'Modal', 'SideModal', 'ToastNotification'],
    options: {
      preventStorage: true,
    },
  },
  {
    path: SPORTS,
    component: Sports,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: SEARCH,
    component: Search,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: SPORT_FILTER,
    component: SportFilter,
    widgets: ['Loading'],
  },
  {
    path: SIGN_UP,
    component: SignUp,
    widgets: ['Modal', 'Loading'],
  },
  {
    path: COLLECTION,
    component: Collection,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: NOT_FOUND,
    component: NotFound,
  },
  {
    path: EVENT_HUB,
    component: EventHub,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: ERROR,
    component: Error,
  },
  {
    path: SETTINGS,
    component: Settings,
    widgets: ['Menu'],
  },
  {
    path: ACCOUNT_DETAILS,
    component: AccountDetails,
    widgets: ['Menu', 'Logout', 'ChangePassword'],
  },
  {
    path: SUBSCRIPTION,
    component: Subscription,
    widgets: ['Menu'],
  },
  {
    path: CUSTOMER_SUPPORT,
    component: CustomerSupport,
    widgets: ['Menu'],
  },
  {
    path: TERMS_OF_SERVICE,
    component: TermsOfService,
    widgets: ['Menu'],
  },
  {
    path: PRIVACY_POLICY,
    component: PrivacyPolicy,
    widgets: ['Menu'],
  },
  {
    path: LIVE_SCORES,
    component: LiveScores,
    widgets: ['Menu'],
  },
  {
    path: DEVELOPER_SETTINGS,
    component: DeveloperSettings,
    widgets: ['ForceCloseWindow'],
  },
  {
    path: FAVORITE_HUB,
    component: FavoriteHub,
    widgets: ['Menu', 'Loading'],
  },
  {
    path: LG_SIGN_UP,
    component: LgSignUp,
  },
  {
    path: VIZIO_SIGN_UP,
    component: VizioSignUp,
  },
  {
    path: 'refresh',
  },
]

export const getRoutes = () => {
  return {
    root: HOME,
    routes: staticRoutes,
    beforeEachRoute: async (from, to) => {
      return to.hash === 'refresh' ? from : true
    },
  }
}

export const createBootRoute = () => {
  return {
    path: BOOT,
    component: Splash,
  }
}

export const redirectHandler = async (deeplink: string) => {
  let endpoint

  if (deeplink.includes('event-hub')) {
    endpoint = encodeURIComponent(composeUrl(deeplink))
    Router.navigate(`${BASE_ROUTES.EVENT_HUB}/${endpoint}`)
    return
  }

  if (deeplink.includes('sport-picker')) {
    Router.navigate(SPORTS)
    return
  }

  if (deeplink.includes('favorite-hub')) {
    endpoint = encodeURIComponent(composeUrl(deeplink))
    Router.navigate(`${BASE_ROUTES.FAVORITE_HUB}/${endpoint}`)
    return
  }

  if (deeplink.includes('search')) {
    const url = deeplink.split('?')[0]
    const urlParams = new URLSearchParams(deeplink.split('?')?.[1])
    const params = Object.fromEntries(urlParams)

    if (isObjEmpty(params)) {
      Router.navigate(BASE_ROUTES.SEARCH)
      return
    }

    endpoint = encodeURIComponent(composeUrl(url, params))
    Router.navigate(`${BASE_ROUTES.SEARCH}/${endpoint}`)
    return
  }

  if (deeplink.includes('collections')) {
    const url = deeplink.split('?')[0]
    const urlParams = new URLSearchParams(deeplink.split('?')?.[1])
    const params = Object.fromEntries(urlParams)

    if (isObjEmpty(params)) {
      Router.navigate(HOME)
      return
    }

    endpoint = encodeURIComponent(composeUrl(url, params))
    Router.navigate(`${BASE_ROUTES.COLLECTION}/${endpoint}`)
    return
  }

  if (
    deeplink.includes('categories') ||
    deeplink.includes('events') ||
    deeplink.includes('continue-watching')
  ) {
    endpoint = encodeURIComponent(composeUrl(deeplink))
    Router.navigate(`${BASE_ROUTES.COLLECTION}/${endpoint}`)
    return
  }

  if (deeplink.includes('edit-favorites')) {
    const parts = deeplink.split('/')
    const tabIndex = parts.indexOf('edit-favorites') + 1
    const tab = parts[tabIndex]

    if (tab) {
      endpoint = encodeURIComponent(composeUrl(deeplink))
      Router.navigate(`${BASE_ROUTES.EDIT_FAVORITES}/${endpoint}`)
    } else {
      Router.navigate(BASE_ROUTES.EDIT_FAVORITES)
    }

    return
  }

  if (deeplink.includes('nav-hub')) {
    const queryString = deeplink.split('?')?.[1]
    const params = new URLSearchParams(queryString)
    const siteId = params.get('site_id')
    const navId = params.get('nav_id')

    if (!siteId || !navId) {
      Router.navigate(HOME)
      return
    }

    Router.navigate(`${BASE_ROUTES.NAV_HUB}/${siteId}/${navId}`)

    return
  }

  if (deeplink.includes('home')) {
    const queryString = deeplink.split('?')?.[1]
    const params = new URLSearchParams(queryString)
    const siteId = params.get('site_id')

    if (!siteId) {
      Router.navigate(HOME)
    } else {
      Router.navigate(`${BASE_ROUTES.HOME}/${siteId}`)
    }

    return
  }

  if (deeplink.startsWith('player')) {
    const { samsungCWService } = AppConfig.shared
    const [, queryParams] = deeplink.split('?')

    const playerData = samsungCWService.fromDeeplinkPayload(queryParams)
    Router.navigate(`${BASE_ROUTES.PLAYER}/tv-video-card-${playerData.node_id}`, playerData)
    return
  }

  // Default
  Router.navigate(HOME)
}
